import { useBlackCardContext } from 'root/src/client/v2/common/components/BlackCard/hooks/useBlackCardContext'
import { useProjectCard } from 'root/src/client/web/list/listItemComponents/project/hooks/useProjectCard'
import moment from 'moment-mini'
import classNames from 'classnames'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import twitterLogo from 'root/src/client/public/twitter-logo-banner.png'
import { addHttpsProtocol } from 'root/src/shared/util/protocolUtil'
import Link from 'root/src/client/web/base/Link'
import ButtonNew from 'root/src/client/web/base/ButtonNew'
import {
	projectDeliveredKey,
	projectDeliveryPendingKey,
	projectPendingKey,
} from 'root/src/shared/constants/keys'
import {
	ProjectCardLabel,
	statusColorsMap,
} from 'root/src/client/web/list/listItemComponents/project/ProjectCardLabel'
import equals from 'ramda/src/equals'
import PlayIcon from 'root/src/client/public/icons/ic-play-new.svg'
import { useState } from 'react'
import useInterval from 'root/src/client/web/hooks/useInterval'
import ClockIcon from 'root/src/client/public/icons/ic-clock.svg'
import Typography, { typographyVariants } from 'root/src/client/web/typography/Typography'
import { colorsNew } from 'root/src/client/web/commonStyles'
import { withProjectCardStyle } from 'root/src/client/web/list/listItemComponents/style'
import { BaseTooltip, BaseTooltipContent } from 'root/src/client/v2/common/components/BaseTooltip/BaseTooltip'
import { getDateWithTimezone } from 'root/src/shared/util/getDateWithTimezone'
import { useCreatorDataInfo } from 'root/src/client/web/record/CreatorData/hooks/useCreatorDataInfo'
import { CommunityReviewStatus } from 'root/src/shared/communityReview/communityReview.types'
import { useIsAdmin } from 'root/src/client/v2/auth/hooks/useIsAdmin'
import { buildAssetUrl, assetSize } from 'root/src/client/v2/common/helpers/assetUrl'

const ProjectHeaderImage = withProjectCardStyle(({ classes }) => {
	const { isHovered } = useBlackCardContext()
	const {
		project: { games, projectUrl },
		extraData: { delivery },
	} = useProjectCard()

	const isTwitterDelivery = delivery?.platform === 'twitter'

	return (
		<LazyLoadImage
			src={
				isTwitterDelivery
					? twitterLogo.src
					: delivery && delivery.thumbnail
						? buildAssetUrl(delivery.thumbnail, { w: assetSize('mobile'), q: 80, fmt: 'webp' })
						: buildAssetUrl(games?.[0]?.boxArtTemplateUrl, { w: assetSize('mobile'), q: 80, fmt: 'webp' })
			}
			alt={projectUrl}
			className={classNames({
				[classes.thumbnail]: delivery?.thumbnail,
				[classes.image]: !delivery?.thumbnail,
				[classes.tiktokThumbnail]: delivery?.platform === 'tiktok',
				[classes.scaledUp]: isHovered,
				[classes.scaledDown]: !isHovered,
				[classes.smOpacity]: true,
			})}
			width="280"
			height="320"
		/>
	)
})

const CtaButton = withProjectCardStyle(({ classes }) => {
	const { project: { brandProduct, landingShortUrl }, actions: { onCtaClick } } = useProjectCard()
	return (
		<div className={classes.ctaButtonOverlay}>
			<Link onClick={onCtaClick} href={addHttpsProtocol(landingShortUrl || brandProduct.url)}>
				<ButtonNew type="secondary">
					{brandProduct?.cta?.toUpperCase()}
				</ButtonNew>
			</Link>
		</div>
	)
})

const MarksContainer = withProjectCardStyle(({ classes }) => {
	const {
		project: { isAccepted, isSuspicious, communityReviews = [], isTwitchPanelDare, status, isPledged },
		extraData: { timeLeft, delivery },
	} = useProjectCard()
	const { isAdmin } = useIsAdmin()

	const isRejected = isAdmin && communityReviews.some(review => review.status === CommunityReviewStatus.Rejected)
	const isPending = status === projectPendingKey
	const isExpired = timeLeft <= 0
	const isDelivered = status === projectDeliveredKey

	return (
		<div className={classes.marksContainer}>
			<div className={classes.status}>
				{isPledged && <ProjectCardLabel status="PLEDGED" color={statusColorsMap.pledged} />}
				{isSuspicious && (
					<ProjectCardLabel status="SUSPICIOUS" color={statusColorsMap.expired} />
				)}
				{isRejected && (
					<ProjectCardLabel status="REJECTED" color={statusColorsMap.expired} />
				)}
				{!delivery && isAccepted && !isPending && !isExpired && (
					<ProjectCardLabel status="ACCEPTED" color={statusColorsMap.accepted} />
				)}
				{!isRejected && delivery && equals(status, projectDeliveryPendingKey) && (
					<ProjectCardLabel status="IN REVIEW" color={statusColorsMap.inReview} />
				)}
				{(isDelivered || (delivery && equals(status, projectDeliveredKey))) && (
					<ProjectCardLabel status="COMPLETED" color={statusColorsMap.delivered} />
				)}
				{(!isDelivered && !delivery && isExpired && !isTwitchPanelDare) && (
					<ProjectCardLabel status="EXPIRED" color={statusColorsMap.expired} />
				)}
				{isPending
					&& <ProjectCardLabel status="PENDING" color={statusColorsMap.pending} />}
			</div>
		</div>
	)
})

const ProjectCardPlayIcon = withProjectCardStyle(({ classes }) => (
	<div className={
		classNames(classes.playIconSpace, classes.playIconContainer)}
	>
		<img src={PlayIcon.src} alt="Play" />
	</div>
))

export const ProjectTimeLeft = withProjectCardStyle(({ classes, timeLeft, expirationDate }) => {
	const [left, setLeft] = useState(Math.max(0, timeLeft))

	useInterval(() => {
		if (left > 0) {
			setLeft(prev => prev - 1)
		}
	}, 1000)

	const timeLeftDuration = moment.duration(left, 'seconds')

	const daysLeft = Math.floor(moment.duration(left, 'seconds').asDays())

	const formattedTimeLeft = daysLeft === 0
		// hh:mm:ss
		? `${String(timeLeftDuration.hours()).padStart(2, '0')}:${String(timeLeftDuration.minutes()).padStart(2, '0')}:${String(timeLeftDuration.seconds()).padStart(2, '0')}`
		: `${daysLeft} DAYS`

	if (typeof left !== 'number') {
		return null
	}

	return (
		<BaseTooltip
			tooltip={<TooltipContent expirationDate={expirationDate} />}
		>
			<div className={classes.projectTimeLeft}>
				<img src={ClockIcon.src} alt="Clock" />
				<div style={{ display: 'flex', flexDirection: 'column' }}>
					<Typography
						color={colorsNew.teal}
						variant={typographyVariants.micro}
					>
						TIME LEFT
					</Typography>
					<Typography
						className={classes.projectTimeLeftValue}
						variant={typographyVariants.smallBold}
					>
						{formattedTimeLeft}
					</Typography>
				</div>
			</div>
		</BaseTooltip>
	)
})

const TooltipContent = ({ expirationDate }) => (
	<BaseTooltipContent>
		Ends at: {getDateWithTimezone(expirationDate)}
	</BaseTooltipContent>
)

export const ProjectCardHeader = withProjectCardStyle(({ classes }) => {
	const { isHovered } = useBlackCardContext()

	const {
		project: { brandProduct, expirationDate },
		extraData: { timeLeft, delivery },
	} = useProjectCard()
	const { isOwnProfile } = useCreatorDataInfo()

	return (
		<div className={classes.projectCardHeader}>
			{!isOwnProfile && isHovered && brandProduct && brandProduct.amount > 0 && <CtaButton />}
			{!isHovered && delivery && <ProjectCardPlayIcon />}
			<div style={{ position: 'absolute', left: 0, top: 0, zIndex: 1, width: '100%', justifyContent: 'space-between', display: 'flex', alignItems: 'center' }}>
				<div style={{ display: 'flex', width: '100%', margin: 10, justifyContent: 'space-between' }}>
					<MarksContainer />
					{timeLeft > 0 && <ProjectTimeLeft timeLeft={timeLeft} expirationDate={expirationDate} />}
				</div>
			</div>
			<ProjectHeaderImage />
		</div>
	)
})
